import React from 'react';
import { ErrorMessage as FormikErrorMessage } from 'formik';
import { defineMessages } from 'react-intl';
import * as Yup from 'yup';
import { intl } from 'hellospa/components/hs-intl-provider';

const messages = defineMessages({
  errorRequried: {
    id: '',
    description: 'Error message for a required field',
    defaultMessage: '{label} is required',
  },
  errorMaxLength: {
    id: '',
    description: 'Error message for exceeding max length',
    defaultMessage: 'Field must be at most {max} characters',
  },
  errorMinLength: {
    id: '',
    description: 'Error messages for not entering minimum required number of characters',
    defaultMessage: 'Enter at least {min} alphanumeric characters.',
  },
  errorUnknown: {
    id: '',
    description: 'Unknown error message',
    defaultMessage: 'Unknown error',
  },
  errorEmailInvalid: {
    id: '',
    description: 'Error message for invalid email',
    defaultMessage: 'Email format is invalid',
  },

  // We don't use these yet.
  errorYupMixedDefault: {
    id: '',
    description: 'Error message for mixed.default',
    defaultMessage: '{path} is invalid',
  },
  errorYupMixedOneOf: {
    id: '',
    description: 'Error message for mixed.oneOf',
    defaultMessage: '{path} must be one of the following values: {values}',
  },
  errorYupMixedNotOneOf: {
    id: '',
    description: 'Error message for mixed.notOneOf',
    defaultMessage: '{path} must not be one of the following values: {values}',
  },
  errorYupMixedDefined: {
    id: '',
    description: 'Error message for mixed.defined',
    defaultMessage: '{path} must be defined',
  },
  errorYupStringLength: {
    id: '',
    description: 'Error message for string.length',
    defaultMessage: '{path} must be exactly {length} characters',
  },
  errorYupStringMatches: {
    id: '',
    description: 'Error message for string.matches',
    defaultMessage: '{path} must match the following: "{regex}"',
  },
  errorYupStringURL: {
    id: '',
    description: 'Error message for string.url',
    defaultMessage: '{path} must be a valid URL',
  },
  errorYupStringTrim: {
    id: '',
    description: 'Error message for string.trim',
    defaultMessage: '{path} must be a trimmed string',
  },
  errorYupStringLowercase: {
    id: '',
    description: 'Error message for string.lowercase',
    defaultMessage: '{path} must be a lowercase string',
  },
  errorYupStringUppercase: {
    id: '',
    description: 'Error message for string.uppercase',
    defaultMessage: '{path} must be a uppercase string',
  },
  errorYupNumberMin: {
    id: '',
    description: 'Error message for number.num',
    defaultMessage: '{path} must be greater than or equal to {min}',
  },
  errorYupNumberMax: {
    id: '',
    description: 'Error message for',
    defaultMessage: '{path} must be less than or equal to {max}',
  },
  errorYupNumberLessThan: {
    id: '',
    description: 'Error message for number.lessThan',
    defaultMessage: '{path} must be less than {less}',
  },
  errorYupNumberMoreThan: {
    id: '',
    description: 'Error message for number.moreThan',
    defaultMessage: '{path} must be greater than {more}',
  },
  errorYupNumberNotEqual: {
    id: '',
    description: 'Error message for number.notEqual',
    defaultMessage: '{path} must be not equal to {notEqual}',
  },
  errorYupNumberPositive: {
    id: '',
    description: 'Error message for number.positive',
    defaultMessage: '{path} must be a positive number',
  },
  errorYupNumberNegative: {
    id: '',
    description: 'Error message for number.negative',
    defaultMessage: '{path} must be a negative number',
  },
  errorYupNumberInteger: {
    id: '',
    description: 'Error message for number.integer',
    defaultMessage: '{path} must be an integer',
  },
  errorYupDateMin: {
    id: '',
    description: 'Error message for date.min',
    defaultMessage: '{path} field must be later than {min}',
  },
  errorYupDateMax: {
    id: '',
    description: 'Error message for',
    defaultMessage: '{path} field must be at earlier than {max}',
  },
  errorYupObjectNoUnknown: {
    id: '',
    description: 'Error message for object.noUnknown',
    defaultMessage: '{path} field has unspecified keys: {unknown}',
  },
  errorYupArrayMin: {
    id: '',
    description: 'Error message for array.min',
    defaultMessage: '{path} field must have at least {min} items',
  },
  errorYupArrayMax: {
    id: '',
    description: 'Error message for array.max',
    defaultMessage: '{path} field must have less than or equal to {max} items',
  },
});

const setLocale: any = Yup.setLocale;

// See https://github.com/jquense/yup/blob/master/src/locale.js
setLocale({
  mixed: {
    required:
      ({ label }:{ label: string }) => (
        intl.formatMessage(messages.errorRequried, { label })
      ),
    default:
      ({ path }:{ path: string }) => (
        intl.formatMessage(messages.errorYupMixedDefault, { path })
      ),
    oneOf:
      ({ path, values }:{ path: string, values: string }) => (
        intl.formatMessage(messages.errorYupMixedOneOf, { path, values })
      ),
    notOneOf:
      ({ path, values }:{ path: string, values: string }) => (
        intl.formatMessage(messages.errorYupMixedNotOneOf, { path, values })
      ),
    // TODO: Skipped mixed.notType since it needs a helper function to construct the message.
    // See https://github.com/jquense/yup/blob/master/src/locale.js#L8
    defined:
      ({ path }:{ path: string }) => (
        intl.formatMessage(messages.errorYupMixedDefined, { path })
      ),
  },
  string: {
    max: ({ max }:{ max: number }) => (
      intl.formatMessage(messages.errorMaxLength, { max })
    ),
    min: ({ min }:{ min: number }) => (
      intl.formatMessage(messages.errorMinLength, { min })
    ),
    email: intl.formatMessage(messages.errorEmailInvalid),
    length:
      ({ path, length }:{ path: string, length: number }) => (
        intl.formatMessage(messages.errorYupStringLength, { path, length })
      ),
    matches:
      ({ path, regex }: { path: string, regex: string }) => (
        intl.formatMessage(messages.errorYupStringMatches, { path, regex })
      ),
    url: ({ path }:{ path: string }) => (
      intl.formatMessage(messages.errorYupStringURL, { path })
    ),
    trim: ({ path }:{ path: string }) => (
      intl.formatMessage(messages.errorYupStringTrim, { path })
    ),
    lowercase: ({ path }:{ path: string }) => (
      intl.formatMessage(messages.errorYupStringLowercase, { path })
    ),
    uppercase: ({ path }:{ path: string }) => (
      intl.formatMessage(messages.errorYupStringUppercase, { path })
    ),
  },
  number: {
    min:
      ({ path, min }:{ path: string, min: number }) => (
        intl.formatMessage(messages.errorYupNumberMin, { path, min })
      ),
    max:
      ({ path, max }:{ path: string, max: number }) => (
        intl.formatMessage(messages.errorYupNumberMax, { path, max })
      ),
    lessThan:
      ({ path, less }:{ path: string, less: number }) => (
        intl.formatMessage(messages.errorYupNumberLessThan, { path, less })
      ),
    moreThan:
      ({ path, more }:{ path: string, more: number }) => (
        intl.formatMessage(messages.errorYupNumberMoreThan, { path, more })
      ),
    notEqual:
      ({ path, notEqual }:{ path: string, notEqual: number }) => (
        intl.formatMessage(messages.errorYupNumberNotEqual, { path, notEqual })
      ),
    positive:
      ({ path }:{ path: string }) => (
        intl.formatMessage(messages.errorYupNumberPositive, { path })
      ),
    negative:
      ({ path }:{ path: string }) => (
        intl.formatMessage(messages.errorYupNumberNegative, { path })
      ),
    integer:
      ({ path }:{ path: string }) => (
        intl.formatMessage(messages.errorYupNumberInteger, { path })
      ),
  },
  date: {
    min:
      ({ path, min }:{ path: string, min: string }) => (
        intl.formatMessage(messages.errorYupDateMin, { path, min })
      ),
    max:
      ({ path, max }:{ path: string, max: string }) => (
        intl.formatMessage(messages.errorYupDateMax, { path, max })
      ),
  },
  object: {
    noUnknown:
      ({ path, unknown }:{ path: string, unknown: string }) => (
        intl.formatMessage(messages.errorYupObjectNoUnknown, { path, unknown })
      ),
  },
  array: {
    min:
      ({ path, min }:{ path: string, min: number }) => (
        intl.formatMessage(messages.errorYupArrayMin, { path, min })
      ),
    max:
      ({ path, max }:{ path: string, max: number }) => (
        intl.formatMessage(messages.errorYupArrayMax, { path, max })
      ),
  },
});

type Props = {
  name: string,
  className?: string,
}


const ErrorMessage = (props: Props) => {
  return (
    <FormikErrorMessage {...props}>
      {(message: string) => {
        return <span {...props}>{message}</span>;
      }}
    </FormikErrorMessage>
  );
};

export default ErrorMessage;
